import React, { useCallback, useEffect, useRef, useState } from "react";
import EsriMap from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import GraphicsLayerView from "@arcgis/core/views/layers/GraphicsLayerView";
import "./portal-boundary-update.css";
import { messages } from "./i18n-messages";
import { DialogBox, DialogBoxFooterType, Loader, RadioButton, RadioButtonGroup } from "~/core";
import { useAppSelector } from "~/store/hooks";
import {
    getFieldInfoState,
    getIsPortalBoundarySyncModalOpen,
    getMapDetailsForPortalBoundarySyncModal,
    getJdBoundaryOverlapsForPortalBoundarySyncModal,
    getJdBoundaryNamesForPortalBoundarySyncModal,
    getShouldUseExistingJdClient,
} from "~/action-panel/components/field-module/selectors";
import { injectIntl, IntlShape } from "@ai360/core/intl";
import { SimpleFillSymbol } from "@arcgis/core/symbols";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import * as esriProjection from "@arcgis/core/geometry/projection";
import { useDispatch } from "react-redux";
import { actions as fieldActions, getFieldInfoModel } from "~/action-panel/components/field-module";
import * as mapToolsActions from "~/map/components/map-tools/actions";
import { Polygon, SpatialReference } from "@arcgis/core/geometry";
import { BasemapUtils, FieldAPI, Geometry, RecAPI } from "@ai360/core";
import { getTheSystemName, getTheUserCompanyGuid, getUser } from "~/login/selectors";
import { MSGTYPE, actions as notificationActions } from "~/notifications";
import { getBasemap } from "~/map/components/map-control/selectors";
import { getToolsetPayload } from "~/map/components/map-tools/selectors";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import { logFirebaseEvent } from "~/utils/firebase";

type OverlappingBoundary = {
    fieldUri: string;
    polygons: any[];
};

type BoundaryForAi360Update = {
    fieldUri: string;
    polygons: Polygon[];
};

interface IPortalBoundaryUpdateProps {
    intl: IntlShape;
}

function PortalBoundaryUpdate({ intl: { formatMessage } }: IPortalBoundaryUpdateProps) {
    const isOpen = useAppSelector(getIsPortalBoundarySyncModalOpen);
    const { latitude, longitude, zoomLevel, fieldPolygons } = useAppSelector(
        getMapDetailsForPortalBoundarySyncModal
    );
    const overlaps = useAppSelector(getJdBoundaryOverlapsForPortalBoundarySyncModal);
    const names = useAppSelector(getJdBoundaryNamesForPortalBoundarySyncModal);
    const fieldInfo = useAppSelector(getFieldInfoState);
    const fieldModel = useAppSelector(getFieldInfoModel);
    const baseMap = useAppSelector(getBasemap);
    const systemName = useAppSelector(getTheSystemName);
    const shouldUseExistingJdClient = useAppSelector(getShouldUseExistingJdClient);
    const fieldName = fieldInfo.fieldModel?.fieldName;
    const fieldGuid = fieldInfo.fieldModel?.fieldGuid;
    const farmName = !fieldInfo.fieldModel?.farmName ? "" : fieldInfo.fieldModel?.farmName + " - ";

    const userGuid = useAppSelector(getUser).userGuid;
    const companyGuid = useAppSelector(getTheUserCompanyGuid);
    const customerGuid = useAppSelector(getToolsetPayload).customerGuid;

    const [boundaryRadioButtonValue, setBoundaryRadioButtonValue] = useState<string>("A");
    const [jdBoundaries, setJdBoundaries] = useState<BoundaryForAi360Update[]>(null);
    const [onlyOneOverlap, setOnlyOneOverlap] = useState<boolean>(false);
    const [jdBoundaryToUpdateAi360, setJdBoundaryToUpdateAi360] = useState<Geometry[]>(null);
    const [ai360BoundaryToUpdateJd, setAi360BoundaryToUpdateJd] = useState<Geometry[]>(null);
    const [chosenFieldUri, setChosenFieldUri] = useState<string>(null);
    const [chosenFieldName, setChosenFieldName] = useState<string>(null);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);

    const dispatch = useDispatch();
    const mapDiv = useRef<HTMLDivElement>(null);

    const ai360FillSymbol = new SimpleFillSymbol({
        color: [227, 139, 79, 0.8],
        outline: {
            color: [255, 255, 255],
            width: 2,
        },
    });

    const jdOpsCenterFillSymbol = new SimpleFillSymbol({
        outline: {
            color: [210, 46, 169],
            width: 3,
        },
    });

    const jdOpsCenterOnlyOneFieldFillSymbol = new SimpleFillSymbol({
        style: "diagonal-cross",
        outline: {
            color: [210, 46, 169],
            width: 3,
        },
    });

    const jdOpsCenterSelectedFillSymbol = new SimpleFillSymbol({
        style: "diagonal-cross",
        outline: {
            color: [210, 46, 169],
            width: 8,
        },
    });

    useEffect(() => {
        if (overlaps.overlaps) {
            const boundarysAndUris: BoundaryForAi360Update[] = [];
            let geometries = [];
            const geometriesForUpdatingAi360 = [];
            if (overlaps.overlaps.length === 1) {
                setChosenFieldUri(overlaps.overlaps[0].fieldUri);
            }
            overlaps.overlaps.forEach((overlappingField: OverlappingBoundary) => {
                const boundaryAndUri: BoundaryForAi360Update = {
                    fieldUri: null,
                    polygons: null,
                };
                overlappingField.polygons.forEach((overlaps) => {
                    const geometry = new Polygon({
                        spatialReference: { wkid: 4326 },
                        rings: overlaps.coordinates,
                    });
                    const projected = esriProjection.project(
                        geometry,
                        new SpatialReference({ wkid: FieldAPI.spatialReference })
                    ) as Polygon;
                    geometries.push(projected);
                    geometriesForUpdatingAi360.push(projected);
                });
                boundaryAndUri.polygons = geometries;
                boundaryAndUri.fieldUri = overlappingField.fieldUri;
                boundarysAndUris.push(boundaryAndUri);
                geometries = [];
            });
            setJdBoundaries(boundarysAndUris);
            if (overlaps.overlaps.length === 1) {
                setJdBoundaryToUpdateAi360(geometriesForUpdatingAi360);
                setOnlyOneOverlap(true);
                setChosenFieldName(names.names[0].fieldName);
            }
        }
    }, [names.names, overlaps.overlaps]);

    const _changeGraphicSymbolsInResponseToClick = useCallback(
        (response: __esri.HitTestResult): void => {
            const jdResults = response.results.filter((x) => x.graphic.attributes?.source === "jd");
            if (jdResults.length > 0) {
                const graphic = jdResults[0].graphic;
                // If it was selected, and they clicked on it again, set it back to regular fill symbol,
                // not just for the individual graphic, but all graphics on the graphicsLayer
                if (graphic.attributes.fieldUri === chosenFieldUri) {
                    const graphicLayer = jdResults[0].graphic.layer as GraphicsLayer;
                    graphicLayer.graphics.forEach((g) => {
                        g.symbol = jdOpsCenterFillSymbol;
                    });
                    setChosenFieldUri(null);
                    setChosenFieldName(null);
                    setJdBoundaryToUpdateAi360(null);
                } else {
                    // Conversely, if it wasn't selected, update the
                    // fill symbols for that entire layer.
                    const graphicLayer = jdResults[0].graphic.layer as GraphicsLayer;
                    graphicLayer.graphics.forEach((g) => {
                        g.symbol = jdOpsCenterSelectedFillSymbol;
                    });
                    setChosenFieldUri(graphic.attributes.fieldUri);
                    setChosenFieldName(graphic.attributes.name);
                    const jdBoundaryUsedToUpdateAi360 = jdBoundaries
                        .filter((b) => b.fieldUri === graphic.attributes.fieldUri)
                        .map((bb) => bb.polygons as Geometry[])
                        .flat();
                    setJdBoundaryToUpdateAi360(jdBoundaryUsedToUpdateAi360);
                }
            }
        },
        [chosenFieldUri, jdBoundaries]
    );

    const _setAllBoundariesToNormalBorder = useCallback(
        (view: MapView, event: any): void => {
            const graphicsLayers = view.allLayerViews.filter((x) => x.layer.type === "graphics");
            if (graphicsLayers.length > 2) {
                graphicsLayers.forEach(async (g) => {
                    const glView = g as GraphicsLayerView;
                    const gr = await glView.queryGraphics();
                    gr.forEach((gra) => {
                        if (gra.attributes?.source === "jd") {
                            gra.symbol = jdOpsCenterFillSymbol;
                        }
                    });
                });
                view.hitTest(event).then((response) => {
                    if (response.results.length) {
                        _changeGraphicSymbolsInResponseToClick(response);
                    }
                });
            }
        },
        [_changeGraphicSymbolsInResponseToClick]
    );

    useEffect(() => {
        if (!isOpen || !mapDiv.current || !overlaps.overlaps || !names.names) {
            return;
        }

        let cancelled = false;
        let animationFrameId: number | null = null;
        let view: MapView | null = null;
        let clickHandle: __esri.Handle | null = null;

        animationFrameId = window.requestAnimationFrame(() => {
            if (cancelled || !mapDiv.current) {
                return;
            }

            const map = new EsriMap({
                basemap: BasemapUtils.getBasemapById(baseMap),
            });

            // add the Ai360 field boundary (or boundaries if it's multi-part)
            const ai360GeometriesForUpdatingJd = [];
            fieldPolygons.forEach((polygon) => {
                const graphicsLayer = new GraphicsLayer();
                const polygonGraphic = new Graphic({
                    geometry: polygon,
                    symbol: ai360FillSymbol,
                    attributes: {
                        source: "ai360",
                    },
                });
                graphicsLayer.add(polygonGraphic);
                map.add(graphicsLayer);
                ai360GeometriesForUpdatingJd.push(polygon);
            });
            setAi360BoundaryToUpdateJd(ai360GeometriesForUpdatingJd);

            // add the JD Ops Center boundaries that overlap, each on a separate
            // graphicsLayer (meaning, multi-polyon fields all on one graphicsLayer)
            overlaps.overlaps.forEach((overlappingField: OverlappingBoundary) => {
                const graphicsLayer = new GraphicsLayer();
                const matchingField = names.names.find(
                    (x) => x.fieldUri === overlappingField.fieldUri
                );

                overlappingField.polygons.forEach((overlap) => {
                    const geometry = new Polygon({
                        spatialReference: { wkid: 4326 },
                        rings: overlap.coordinates,
                    });
                    const polygonGraphic = new Graphic({
                        geometry: geometry,
                        symbol: onlyOneOverlap
                            ? jdOpsCenterOnlyOneFieldFillSymbol
                            : jdOpsCenterFillSymbol,
                        attributes: {
                            source: "jd",
                            name: matchingField?.fieldName,
                            fieldUri: overlappingField.fieldUri,
                        },
                    });
                    graphicsLayer.add(polygonGraphic);
                });
                map.add(graphicsLayer);
            });

            view = new MapView({
                map: map,
                container: mapDiv.current,
                center: [longitude, latitude],
                zoom: zoomLevel,
                constraints: {
                    rotationEnabled: false,
                    snapToZoom: true,
                },
                ui: {
                    components: ["attribution"],
                },
                navigation: {
                    momentumEnabled: false,
                },
            });

            clickHandle = view.on("click", (event) => {
                if (!onlyOneOverlap && view) {
                    // Set all jd boundaries to have a normal border (not selected)
                    _setAllBoundariesToNormalBorder(view, event);
                }
            });
        });

        return () => {
            cancelled = true;
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            clickHandle?.remove();
            if (view) {
                view.destroy();
            }
        };
    }, [
        isOpen,
        baseMap,
        fieldPolygons,
        latitude,
        longitude,
        names.names,
        onlyOneOverlap,
        overlaps.overlaps,
        _setAllBoundariesToNormalBorder,
        zoomLevel,
    ]);

    const confirmUpdateBoundary = async () => {
        logFirebaseEvent("portal_update_jd_ops");
        setShowLoader(true);
        setShowConfirmationDialog(false);
        dispatch(mapToolsActions.setChosePortalBoundary(false));
        const geometriesToUpdateJd = ai360BoundaryToUpdateJd.map((x) =>
            webMercatorUtils.webMercatorToGeographic(x).toJSON()
        );
        const request = {
            geometries: JSON.stringify(geometriesToUpdateJd),
            fieldUri: chosenFieldUri,
            userGuid: userGuid,
            fieldGuid: fieldGuid,
            shouldUseExistingJdClient: shouldUseExistingJdClient,
        };
        const response = await RecAPI.mergeJdBoundaryAndExisting(
            request,
            companyGuid,
            customerGuid
        );
        if (!response.success) {
            dispatch(notificationActions.pushToasterMessage(response.message, MSGTYPE.ERROR));
        } else {
            if (shouldUseExistingJdClient) {
                dispatch(
                    fieldActions.updateFieldInfo({ ...fieldModel, fieldName: chosenFieldName })
                );
            }
            dispatch(notificationActions.pushToasterMessage(response.message, MSGTYPE.SUCCESS));
        }
        // cleanup
        setChosenFieldName(null);
        setJdBoundaries([]);
        setOnlyOneOverlap(false);
        setJdBoundaryToUpdateAi360([]);
        setAi360BoundaryToUpdateJd([]);
        setChosenFieldUri(null);
        setShowLoader(false);
        dispatch(fieldActions.setPortalBoundarySyncModalOpen(false));
        dispatch(fieldActions.setJdBoundariesForBoundarySyncModal([], []));
        dispatch(fieldActions.setShouldUseExistingJdClient(false));
        // ---
    };

    const updateBoundary = async () => {
        if (boundaryRadioButtonValue == "J") {
            logFirebaseEvent("portal_use_jd_ops");
            setShowLoader(true);
            dispatch(mapToolsActions.setChosePortalBoundary(true));
            dispatch(mapToolsActions.clearFieldEditGeometries());

            dispatch(mapToolsActions.setFieldEditGeometries(jdBoundaryToUpdateAi360));
            dispatch(mapToolsActions.setFieldEditsChanged(true));
            const request = {
                geometries: null,
                fieldUri: chosenFieldUri,
                userGuid: userGuid,
                fieldGuid: fieldGuid,
                shouldUseExistingJdClient: shouldUseExistingJdClient,
            };
            const response = await RecAPI.mergeJdBoundaryAndExisting(
                request,
                companyGuid,
                customerGuid
            );
            if (!response.success) {
                dispatch(notificationActions.pushToasterMessage(response.message, MSGTYPE.ERROR));
            } else {
                if (shouldUseExistingJdClient) {
                    dispatch(
                        fieldActions.updateFieldInfo({ ...fieldModel, fieldName: chosenFieldName })
                    );
                }
                dispatch(notificationActions.pushToasterMessage(response.message, MSGTYPE.SUCCESS));
            }
            // cleanup
            setChosenFieldName(null);
            setJdBoundaries([]);
            setOnlyOneOverlap(false);
            setJdBoundaryToUpdateAi360([]);
            setAi360BoundaryToUpdateJd([]);
            setChosenFieldUri(null);
            setShowLoader(false);
            dispatch(fieldActions.setPortalBoundarySyncModalOpen(false));
            dispatch(fieldActions.setJdBoundariesForBoundarySyncModal([], []));
            // ---
        } else {
            setShowConfirmationDialog(true);
        }
    };
    return (
        <div className="main-dialog">
            <DialogBox
                className="portal-boundary-update"
                action="Update"
                actionDisabled={
                    boundaryRadioButtonValue === null ||
                    jdBoundaryToUpdateAi360 === null ||
                    chosenFieldUri === null
                }
                draggable={true}
                isOpen={isOpen}
                unrestricted={true}
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                title={formatMessage(messages.title, { farm: farmName, field: fieldName })}
                onAction={() => {
                    updateBoundary();
                }}
                onClose={() => {
                    logFirebaseEvent("portal_cancel");
                    setChosenFieldName(null);
                    setJdBoundaries([]);
                    setOnlyOneOverlap(false);
                    setJdBoundaryToUpdateAi360([]);
                    setAi360BoundaryToUpdateJd([]);
                    setChosenFieldUri(null);
                    setShowLoader(false);
                    dispatch(fieldActions.setPortalBoundarySyncModalOpen(false));
                    dispatch(fieldActions.setJdBoundariesForBoundarySyncModal([], []));
                }}
            >
                {showLoader && <Loader />}
                <RadioButtonGroup
                    className={"side-by-side-radio"}
                    selectedValue={boundaryRadioButtonValue}
                    afterOnChange={(value) => {
                        setBoundaryRadioButtonValue(value.toString());
                    }}
                >
                    <div className="icon-holder">
                        <div className="system-boundary-icon"></div>
                    </div>
                    <RadioButton
                        className={"ai-radio"}
                        label={formatMessage(messages.updateJdOpsCenterBoundary, {
                            system: systemName,
                            jdFieldName:
                                !chosenFieldName || boundaryRadioButtonValue == "J"
                                    ? ""
                                    : ` (${chosenFieldName})`,
                        })}
                        value={"A"}
                    />
                    <div className="icon-holder">
                        <div className="jd-boundary-icon"></div>
                    </div>
                    <RadioButton
                        className={"jd-radio"}
                        label={formatMessage(messages.updateAi360Boundary, {
                            system: systemName,
                            jdFieldName:
                                !chosenFieldName || boundaryRadioButtonValue == "A"
                                    ? ""
                                    : ` (${chosenFieldName})`,
                        })}
                        value={"J"}
                    />
                </RadioButtonGroup>
                <div className="portal-boundary-map-holder">
                    <div ref={mapDiv} className="portal-boundary-map-div"></div>
                </div>
            </DialogBox>
            {showConfirmationDialog ? (
                <div className="confirmation-dialog">
                    <DialogBox
                        className="confirmation-modal"
                        isOpen={isOpen}
                        unrestricted={true}
                        footerType={DialogBoxFooterType.YES_NO}
                        title={formatMessage(messages.confirmationTitle)}
                        onAction={() => {
                            confirmUpdateBoundary();
                        }}
                        onClose={() => {
                            setShowConfirmationDialog(false);
                        }}
                    >
                        {formatMessage(messages.confirmationMessage)}
                    </DialogBox>
                </div>
            ) : null}
        </div>
    );
}

export default injectIntl(PortalBoundaryUpdate);
